import { addDays, addMonths, format as formatFns, isValid, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'

/**
 * Tipos de entrada de fecha soportados
 */
type DateInput = Date | string | null | undefined

// Texto que moment (con locale es) devolvía al formatear una fecha inválida;
// date-fns lanza RangeError, así que preservamos el comportamiento anterior
const FECHA_INVALIDA = 'Fecha inválida'

/**
 * Parseo equivalente a moment(string): primero ISO 8601 (los strings solo-fecha
 * se interpretan en hora local, igual que moment) y como último recurso el
 * constructor nativo de Date
 */
const parsearString = (valor: string): Date => {
  const iso = parseISO(valor)

  if (isValid(iso)) {
    return iso
  }

  return new Date(valor)
}

/**
 * Convierte string ISO, timestamp o Date a Date, con la misma semántica que
 * moment(x).toDate(): strings solo-fecha en hora local, undefined = ahora,
 * null = fecha inválida
 */
export const toDate = (valor?: string | number | Date | null): Date => {
  if (valor === undefined) {
    return new Date()
  }
  if (valor === null) {
    return new Date(Number.NaN)
  }
  if (valor instanceof Date) {
    return valor
  }
  if (typeof valor === 'number') {
    return new Date(valor)
  }

  return parsearString(valor)
}

/**
 * Formatea con locale es y sin lanzar excepción ante fechas inválidas
 * (moment devolvía 'Fecha inválida' en ese caso)
 */
const formatear = (valor: string | Date | null | undefined, formato: string): string => {
  // Un valor AUSENTE no es hoy. `toDate` devuelve la fecha actual para `undefined` -por
  // compatibilidad con moment, ver su comentario-, asi que sin este corte un campo que no vino se
  // pintaba con la fecha de hoy como si fuera el dato real. Es peor que no mostrar nada: se lee
  // como informacion cierta.
  //
  // Se corta aca, en el formateo, y no en `toDate`: quien la usa para calcular mantiene la
  // semantica que ya tenia.
  if (valor === undefined || valor === null) {
    return FECHA_INVALIDA
  }

  const fecha = toDate(valor)

  if (!isValid(fecha)) {
    return FECHA_INVALIDA
  }

  return formatFns(fecha, formato, { locale: es })
}

const pad2 = (valor: number): string => String(valor).padStart(2, '0')

/**
 * Extrae horas y minutos de una duración ISO 8601 (ej: 'PT14H30M', 'PT45M')
 */
const duracionISO = (strDate: string): { horas: number; minutos: number } => {
  const match = /PT(?:(\d+)H)?(?:(\d+)M)?/.exec(strDate)

  return {
    horas: Number(match?.[1] ?? 0),
    minutos: Number(match?.[2] ?? 0),
  }
}

/**
 * Parsea de forma segura cualquier tipo de fecha a un objeto Date
 * Soporta:
 * - Objetos Date nativos
 * - ISO strings (2025-06-19T00:00:00-05:00)
 * - Strings en formato dd/MM/yyyy o dd/MM/yy
 * - Timestamps numéricos
 *
 * @param dateInput - La fecha en cualquier formato soportado
 * @returns Date object o null si no es válida
 */
export const safeParseDate = (dateInput: DateInput): Date | null => {
  // Caso 1: null o undefined
  if (!dateInput) {
    return null
  }

  // Caso 2: Ya es un Date object
  if (dateInput instanceof Date) {
    return Number.isNaN(dateInput.getTime()) ? null : dateInput
  }

  // Caso 3: String
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim()

    if (!trimmed || trimmed === '') {
      return null
    }

    // Intentar formato dd/MM/yyyy o dd/MM/yy primero
    const ddmmyyyyRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/
    const match = ddmmyyyyRegex.exec(trimmed)

    if (match) {
      const day = Number.parseInt(match[1], 10)
      const month = Number.parseInt(match[2], 10)
      let year = Number.parseInt(match[3], 10)

      // Si el año es de 2 dígitos, convertir al siglo XXI
      if (year < 100) {
        year += 2000
      }

      // Validaciones básicas
      if (month < 1 || month > 12 || day < 1 || year < 1900 || year > 2099) {
        return null
      }

      const date = new Date(year, month - 1, day)

      // Verificar que la fecha sea válida (no se desbordó)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null
      }

      return date
    }

    // Intentar parsear como ISO string o cualquier otro formato estándar
    const parsed = parsearString(trimmed)

    if (isValid(parsed)) {
      return parsed
    }
  }

  return null
}

/**
 * Convierte de forma segura una fecha a un formato específico
 *
 * @param dateInput - La fecha en cualquier formato soportado
 * @param format - Formato de salida de date-fns (ej: 'yyyy-MM-dd', 'dd/MM/yyyy')
 * @param fallback - Valor de retorno si la fecha es inválida (default: '')
 * @returns String formateado o fallback
 */
export const safeDateToFormat = (dateInput: DateInput, format: string, fallback: string = ''): string => {
  const parsedDate = safeParseDate(dateInput)

  if (!parsedDate) {
    return fallback
  }

  return formatear(parsedDate, format)
}

export const iso8601ToHHmm = (strDate: string): string => {
  const { horas, minutos } = duracionISO(strDate)

  return `${pad2(horas)}:${pad2(minutos)}`
}

export const iso8601ToHHmmVuelos = (strDate: string): string | undefined => {
  if (strDate !== null && strDate !== undefined) {
    const { horas, minutos } = duracionISO(strDate)

    if (strDate.includes('H')) {
      return `${pad2(horas)}:${pad2(minutos)}`
    }

    return `00:${pad2(minutos)}`
  }
}

export const converHHmmToPTHHMM = (strHHMM: string): string => {
  const tiempo = strHHMM.split(':')

  return `PT${tiempo[0]}H${tiempo[1]}M`
}

export const dateTimeToDate = (dateTime: string | Date | null | undefined): string => {
  return formatear(dateTime, 'dd/MM/yyyy')
}

export const stringToDiaFecha = (fecha: string | null | undefined): string => {
  return formatear(fecha, 'EEE dd/MM/yyyy')
}

export const dateTimeToDateForms = (dateTime: string | Date | null | undefined): string => {
  return formatear(dateTime, 'yyyy-MM-dd')
}

/**
 * Cota superior EXCLUSIVA para filtros OData sobre campos que llevan hora:
 * `le yyyy-MM-dd` compara contra las 00:00 de ese día y deja fuera todo lo
 * ocurrido durante la jornada, así que se devuelve el día siguiente para
 * usarlo con `lt`. Se prefiere esto a mandar 23:59:59 porque no depende del
 * huso con el que el backend interprete la hora.
 */
export const dateTimeToDateFormsFinExclusivo = (dateTime: string | Date): string => {
  return formatear(addDays(toDate(dateTime), 1), 'yyyy-MM-dd')
}

export const iso8601Date = (dateTime: string | Date): string => {
  return formatear(dateTime, `yyyy-MM-dd'T'HH:mm:ss.SSS`) + 'Z'
}

export const dateTimeAuditoria = (dateTime: string | Date): string => {
  return formatear(dateTime, 'dd-MM-yyyy HH:mm:ss')
}

export const dateTimeAuditoriaLite = (dateTime: string | Date): string => {
  return formatear(dateTime, 'dd/MM/yy HH:mm')
}

export const dateTimeToDateTime = (dateTime: string | Date): string => {
  return formatear(dateTime, 'dd/MM/yyyy HH:mm:ss')
}

export const dateToDDMMYYY = (date?: Date): string => {
  // moment(undefined) devolvía la fecha actual; se preserva ese comportamiento
  return formatear(date ?? new Date(), 'dd/MM/yyyy')
}

export const dateToNotificationFormat = (date?: string | Date): string => {
  return formatear(date ?? new Date(), 'dd-MM-yyyy')
}

export const dateTimeToHHmm = (date: Date): string => {
  return formatear(date, 'HH:mm')
}

export const dateToMMYYY = (date: Date): string => {
  return formatear(date, 'MM/yyyy')
}

export const dateToDDMM = (date: Date): string => {
  return formatear(date, 'dd/MM')
}

/**
 * Fecha lista para mandar a la API, o `null` cuando no hay una fecha válida.
 *
 * `dateToYY_MM_DD` devuelve el texto «Fecha inválida» cuando el valor falta o no se puede parsear
 * —se conservó ese comportamiento de moment a propósito, para no cambiar lo que se PINTA en
 * pantalla—. Pero ese texto no sirve para un PUT: la API lo recibe donde espera una fecha y responde
 * 400 con un mensaje que no le dice nada al usuario.
 *
 * Esto importa desde que «Fecha de emisión» se puede editar en el grid de boletos emitidos: si
 * alguien limpia el campo, el DatePicker deja `null` y sin este corte se enviaría el literal.
 */
export const fechaParaApi = (valor: DateInput): string | null => {
  if (valor === null || valor === undefined) {
    return null
  }

  const fecha = toDate(valor)

  return isValid(fecha) ? formatFns(fecha, 'yyyy-MM-dd') : null
}

// eslint-disable-next-line camelcase
export const dateToYY_MM_DD = (fecha: Date): string => {
  return formatear(fecha, 'yyyy-MM-dd')
}

export const dateTimeMatriz = (dateTime: string | Date): string => {
  return formatear(dateTime, 'yyyy-MM-dd HH:mm:ss')
}

export const dateTimeMinutos = (dateTime: string | Date): string => {
  return formatear(dateTime, 'dd-MM-yyyy HH:mm')
}

export const dateToNameDay = (date: Date): string => {
  const fecha = new Date(date)
  const nombreDia = fecha.toLocaleString('es-ES', { weekday: 'long' })

  return nombreDia.charAt(0).toUpperCase() + nombreDia.slice(1)
}

export const toStringToDate = (date: string): Date => {
  return parsearString(date)
}

// Milisegundos de un año promedio, mismo factor que usaba moment.duration().asYears()
const MS_POR_ANIO = 86400000 * 365.2425

export const calcularDuracionByExpiracion = (fechaExpiracion: string | Date): number => {
  const fecha = fechaExpiracion instanceof Date ? fechaExpiracion : parsearString(fechaExpiracion)
  const anios = (fecha.getTime() - Date.now()) / MS_POR_ANIO

  if (anios <= 1) {
    return 2
  }

  return Math.ceil(anios) + 1
}

/**
 * Diferencia en meses con fracción, replicando el algoritmo de anclas de
 * moment.diff(fecha, 'months', true): la parte entera son meses calendario y
 * la fracción se prorratea entre los meses ancla adyacentes
 */
const diferenciaMesesFraccional = (inicio: Date, fin: Date): number => {
  const mesesEnteros = (fin.getFullYear() - inicio.getFullYear()) * 12 + (fin.getMonth() - inicio.getMonth())
  const ancla = addMonths(inicio, mesesEnteros)

  if (fin.getTime() - ancla.getTime() < 0) {
    const anclaAnterior = addMonths(inicio, mesesEnteros - 1)

    return mesesEnteros + (fin.getTime() - ancla.getTime()) / (ancla.getTime() - anclaAnterior.getTime())
  }

  const anclaSiguiente = addMonths(inicio, mesesEnteros + 1)

  return mesesEnteros + (fin.getTime() - ancla.getTime()) / (anclaSiguiente.getTime() - ancla.getTime())
}

export const validarIntervaloFechasByMeses = (fechaInicio: Date, fechaFin: Date, meses: number): boolean => {
  const diferenciaMeses = diferenciaMesesFraccional(fechaInicio, fechaFin)

  // Verifica si la diferencia es menor o igual a los meses permitidos
  return diferenciaMeses <= meses
}

export const dateBeforeMonths = (months: number): Date => {
  const now = new Date()
  const twoMonthsBefore = new Date()

  twoMonthsBefore.setMonth(now.getMonth() - months)

  return twoMonthsBefore
}

export const dateAfterMonths = (months: number): Date => {
  const now = new Date()
  const twoMonthsAfter = new Date()

  twoMonthsAfter.setMonth(now.getMonth() + months)

  return twoMonthsAfter
}

/**
 * Si dos rangos de fechas se pisan, aunque sea un instante.
 *
 * Dos rangos se solapan cuando cada uno empieza antes de que termine el otro. Esa sola condicion
 * cubre los cuatro casos: uno dentro del otro, cruzados por cualquiera de los dos lados, extremos
 * compartidos con solape real y —el que se suele olvidar— uno que ENVUELVE al otro, donde ninguno
 * de sus dos extremos cae dentro del contrario.
 *
 * Los bordes son ESTRICTOS a proposito: cuando el fin de un rango es exactamente el inicio del
 * otro NO se solapan. Es la regla del negocio en la matriz de barcos (el unico consumidor,
 * confirmada por David el 21-ago-2026): el guia desembarca y embarca EL MISMO DIA — el
 * back-to-back entre salidas encadenadas es la operacion normal y no debe bloquearse. Con bordes
 * inclusivos, asignar una salida pintaba de ocupado a la anterior y la siguiente (bug #40970).
 * Compartir el dia de inicio o el de fin con solape de mas de ese instante sigue contando, porque
 * ahi el guia si esta en dos salidas a la vez.
 */
export const seSolapan = (
  inicioA: string | Date,
  finA: string | Date,
  inicioB: string | Date,
  finB: string | Date,
): boolean => {
  const a1 = toDate(inicioA).getTime()
  const a2 = toDate(finA).getTime()
  const b1 = toDate(inicioB).getTime()
  const b2 = toDate(finB).getTime()

  return a1 < b2 && b1 < a2
}
